import { ReactNode, useRef, useCallback, useEffect } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { useIsDesktop, usePrefersReducedMotion } from "@/hooks/useMediaQuery";

interface MagneticButtonProps {
  children: ReactNode;
  className?: string;
  strength?: number;
  radius?: number;
}

/**
 * Wraps children in a magnetic hover container.
 * On desktop, the inner element gently follows the cursor within `radius`.
 * No-op on touch devices and when reduced motion is preferred.
 *
 * Performance: caches getBoundingClientRect on pointer enter (and invalidates
 * on scroll/resize) so the per-mousemove handler never reads layout —
 * eliminating forced reflows.
 */
export function MagneticButton({
  children,
  className,
  strength = 0.35,
  radius = 90,
}: MagneticButtonProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();
  const reduced = usePrefersReducedMotion();
  const enabled = isDesktop && !reduced;

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 200, damping: 18, mass: 0.4 });
  const springY = useSpring(y, { stiffness: 200, damping: 18, mass: 0.4 });

  const cachedRectRef = useRef<DOMRect | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const hoveringRef = useRef(false);

  // Invalidate cached rect when layout may have changed.
  useEffect(() => {
    if (!enabled) return;
    const invalidate = () => {
      cachedRectRef.current = null;
    };
    window.addEventListener("scroll", invalidate, { passive: true });
    window.addEventListener("resize", invalidate, { passive: true });
    return () => {
      window.removeEventListener("scroll", invalidate);
      window.removeEventListener("resize", invalidate);
    };
  }, [enabled]);

  const handleEnter = useCallback(() => {
    if (!enabled || !ref.current) return;
    hoveringRef.current = true;
    cachedRectRef.current = ref.current.getBoundingClientRect();
  }, [enabled]);

  const handleMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!enabled) return;
      const clientX = e.clientX;
      const clientY = e.clientY;
      if (rafIdRef.current !== null) return;
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        if (!hoveringRef.current) return;
        let rect = cachedRectRef.current;
        if (!rect && ref.current) {
          rect = ref.current.getBoundingClientRect();
          cachedRectRef.current = rect;
        }
        if (!rect) return;
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = clientX - cx;
        const dy = clientY - cy;
        const dist = Math.hypot(dx, dy);
        if (dist > radius) {
          x.set(0);
          y.set(0);
          return;
        }
        x.set(dx * strength);
        y.set(dy * strength);
      });
    },
    [enabled, radius, strength, x, y],
  );

  const handleLeave = useCallback(() => {
    hoveringRef.current = false;
    cachedRectRef.current = null;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    x.set(0);
    y.set(0);
  }, [x, y]);

  return (
    <div
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={className}
      style={{ display: "inline-block" }}
    >
      <motion.div style={{ x: springX, y: springY }}>{children}</motion.div>
    </div>
  );
}
